
import { Coins, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

interface CreditDiscountSectionProps {
  userCredits: number;
  maxCreditsUsable: number;
  creditsToUse: number;
  onCreditsChange: (credits: number) => void;
}

export function CreditDiscountSection({
  userCredits,
  maxCreditsUsable,
  creditsToUse,
  onCreditsChange
}: CreditDiscountSectionProps) {
  const handleSliderChange = (value: number[]) => {
    onCreditsChange(value[0]);
  };

  const adjustCredits = (delta: number) => {
    const newValue = Math.max(0, Math.min(maxCreditsUsable, creditsToUse + delta));
    onCreditsChange(newValue);
  };

  return (
    <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/20">
      <div className="flex items-center gap-2 text-yellow-400 mb-3">
        <Coins className="w-5 h-5" />
        <h3 className="text-lg font-display font-semibold">Use Your Credits</h3>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-white/70">Available Credits</span>
          <span className="text-yellow-400 font-medium">{userCredits} credits</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-white/70">Max Usable (80% of price)</span>
          <span className="text-white/70">{maxCreditsUsable} credits</span>
        </div>

        {/* Credit Slider */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => adjustCredits(-1)}
              disabled={creditsToUse <= 0}
              className="h-8 w-8 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
            >
              <Minus className="w-4 h-4" />
            </Button>

            <div className="flex-1">
              <Slider
                value={[creditsToUse]}
                onValueChange={handleSliderChange}
                max={maxCreditsUsable}
                min={0}
                step={1}
                className="w-full"
              />
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={() => adjustCredits(1)}
              disabled={creditsToUse >= maxCreditsUsable}
              className="h-8 w-8 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          <div className="text-center">
            <span className="text-white font-medium">
              Using {creditsToUse} credits = ${creditsToUse.toFixed(2)} discount
            </span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCreditsChange(0)}
            className="border-white/20 text-white/70 hover:bg-white/10 text-xs"
          >
            Use None
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCreditsChange(maxCreditsUsable)}
            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20 text-xs"
          >
            Use Max
          </Button>
        </div>
      </div>
    </div>
  );
}
